/**
 * ServerMetricsLiveChart — Recharts CPU/RAM/Disk + polling 10s (actif si status=active).
 *
 * Seuils visuels :
 *   < 70% = vert, 70-85% = amber, >= 85% = red (+ toast session-unique).
 *
 * @spec specs/app/admin/dedicated-servers.md §6.3
 */

import { Alert, App as AntdApp } from 'antd';
import { Cpu, HardDrive, MemoryStick } from 'lucide-react';
import { useEffect, useRef, type FC } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useDedicatedServerMetrics } from '../hooks/useDedicatedServerMetrics';
import type { ServerMetrics } from '@/types/dedicatedServer';

interface ServerMetricsLiveChartProps {
  serverId: string;
  serverName?: string;
  enabled: boolean;
}

const seuil = (pct: number): 'ok' | 'warn' | 'alert' => {
  if (pct >= 85) return 'alert';
  if (pct >= 70) return 'warn';
  return 'ok';
};

const colorFor = (level: 'ok' | 'warn' | 'alert'): string => {
  if (level === 'alert') return 'text-red-600 dark:text-red-400';
  if (level === 'warn') return 'text-amber-600 dark:text-amber-400';
  return 'text-emerald-600 dark:text-emerald-400';
};

const MetricMiniCard: FC<{ label: string; value: number; icon: FC<{ className?: string }> }> = ({
  label,
  value,
  icon: Icon,
}) => {
  const level = seuil(value);
  return (
    <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div
        className={`flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 dark:bg-slate-800 ${colorFor(
          level,
        )}`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <p className={`text-lg font-bold ${colorFor(level)}`}>{value.toFixed(0)}%</p>
      </div>
    </div>
  );
};

const ServerMetricsLiveChart: FC<ServerMetricsLiveChartProps> = ({
  serverId,
  serverName,
  enabled,
}) => {
  const { message } = AntdApp.useApp();
  const { data, isLoading, isError } = useDedicatedServerMetrics(serverId, { enabled });
  const alertedRef = useRef<{ cpu: boolean; ram: boolean; disk: boolean }>({
    cpu: false,
    ram: false,
    disk: false,
  });

  const metrics = (data?.data as { data?: ServerMetrics } | ServerMetrics | undefined) ?? undefined;
  const m: ServerMetrics | undefined =
    metrics && 'cpu_percent' in metrics
      ? metrics
      : (metrics as { data?: ServerMetrics } | undefined)?.data;

  // Alerte session-unique si franchissement 85%
  useEffect(() => {
    if (!m) return;
    const target = serverName ?? serverId;
    if (m.cpu_percent >= 85 && !alertedRef.current.cpu) {
      void message.warning(`CPU > 85% sur ${target}`);
      alertedRef.current.cpu = true;
    }
    if (m.ram_percent >= 85 && !alertedRef.current.ram) {
      void message.warning(`RAM > 85% sur ${target}`);
      alertedRef.current.ram = true;
    }
    if (m.disk_percent >= 85 && !alertedRef.current.disk) {
      void message.warning(`Disque > 85% sur ${target}`);
      alertedRef.current.disk = true;
    }
  }, [m, message, serverId, serverName]);

  if (!enabled) {
    return (
      <div className="fc-card p-5">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Métriques live
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Les métriques ne sont disponibles que pour les serveurs actifs.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="fc-card p-5">
        <h3 className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Métriques live
        </h3>
        <Alert
          type="warning"
          showIcon
          message="Métriques indisponibles"
          description="L'endpoint admin de métriques n'est pas accessible. Vérifier l'implémentation backend GET /api/v1/admin/dedicated-servers/:id/metrics."
        />
      </div>
    );
  }

  const series = m?.series ?? [];

  return (
    <div className="fc-card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Métriques live
        </h3>
        <span className="text-[11px] text-slate-400">
          Seuil d'alerte 85% · rafraîchi toutes les 10s
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricMiniCard label="CPU" value={m?.cpu_percent ?? 0} icon={Cpu} />
        <MetricMiniCard label="RAM" value={m?.ram_percent ?? 0} icon={MemoryStick} />
        <MetricMiniCard label="Disque" value={m?.disk_percent ?? 0} icon={HardDrive} />
      </div>

      <div className="mt-4 h-64">
        {series.length === 0 && isLoading ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            Collecte des métriques…
          </div>
        ) : series.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-slate-400">
            Pas encore de données (30 dernières minutes).
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={(ts) =>
                  new Date(ts).toLocaleTimeString('fr-FR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }
                stroke="#94a3b8"
                fontSize={11}
              />
              <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={11} unit="%" />
              <ReTooltip
                labelFormatter={(ts) => new Date(ts as string).toLocaleTimeString('fr-FR')}
                formatter={(v) => `${Number(v).toFixed(1)}%`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="cpu"
                name="CPU"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="ram"
                name="RAM"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="disk"
                name="Disque"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default ServerMetricsLiveChart;
